import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import {
  PAYROLL_BATCH_STATUSES,
  type PayrollBatchStatus,
} from "@/features/payroll/api/enums";
import type { PayrollBatchListItemDto } from "@/features/payroll/api/dto";
import { PayrollBatchRowActions } from "@/features/payroll/components/batches/payroll-batch-row-actions";
import { AmountMinor } from "@/features/shared/components/amount-minor";

/** Ligne du tableau : `DataTableClar` exige un `id` requis. */
export type PayrollBatchRow = PayrollBatchListItemDto & { id: string };

const STATUS_VARIANTS: Record<
  PayrollBatchStatus,
  "default" | "secondary" | "success" | "warning" | "destructive"
> = {
  DRAFT: "secondary",
  SIMULATED: "default",
  PROCESSING: "warning",
  COMPLETED: "success",
  PARTIALLY_COMPLETED: "warning",
  FAILED: "destructive",
  CANCELLED: "secondary",
};

const statusLabel = (status: string) =>
  t(`payroll.enums.batchStatus.${status}`);
const feePayerLabel = (payer: string) =>
  t(`payroll.enums.feePayer.${payer}`);

/**
 * Colonnes de la liste paginée des paies (`POST /PayrollBatches/all`).
 * Filtre côté API : `status` (select), `from`/`to` (plage de dates — exposés
 * côté page via un filtre dédié). Les autres colonnes sont `denied`.
 */
export const payrollBatchesColumns: ColumnsClarFactory<PayrollBatchRow> = (
  navigate
) => [
  {
    id: "label",
    accessorKey: "label",
    header: t("payroll.batches.columns.label"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("payroll.batches.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as PayrollBatchStatus | undefined;
      return status ? (
        <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>
          {statusLabel(status)}
        </Badge>
      ) : (
        "—"
      );
    },
    meta: {
      type: "select",
      options: PAYROLL_BATCH_STATUSES.map((value) => ({
        value,
        label: statusLabel(value),
      })),
    },
  },
  {
    id: "feePayer",
    accessorKey: "feePayer",
    header: t("payroll.batches.columns.feePayer"),
    cell: ({ getValue }) => {
      const payer = getValue() as string | undefined;
      return payer ? feePayerLabel(payer) : "—";
    },
    meta: { type: "denied" },
  },
  {
    id: "lineCount",
    accessorKey: "lineCount",
    header: t("payroll.batches.columns.lineCount"),
    cell: ({ row }) => (
      <NoTranslate as="span">
        {row.original.paidCount}
        {" / "}
        {row.original.lineCount}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "grossAmountMinor",
    accessorKey: "grossAmountMinor",
    header: t("payroll.batches.columns.grossAmount"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.grossAmountMinor}
        currency={row.original.currency}
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "debitAmountMinor",
    accessorKey: "debitAmountMinor",
    header: t("payroll.batches.columns.debitAmount"),
    cell: ({ row }) => (
      <AmountMinor
        amountMinor={row.original.debitAmountMinor}
        currency={row.original.currency}
        className="font-medium"
      />
    ),
    meta: { type: "denied" },
  },
  {
    id: "createdAt",
    accessorKey: "createdAt",
    header: t("payroll.batches.columns.createdAt"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="text-xs">
        {formatDateWithFallback(getValue() as string | null, "—")}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <PayrollBatchRowActions batch={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
